/**
 * Bigens legal page (T23): tidies merchant-pasted legal text, numbers its sections and builds the table of contents.
 *
 * Store policies arrive in many shapes: real h2/h3 headings (Shopify's privacy template), or bold lines separated by
 * <br><br> without any paragraphs (Shopify's terms template). This component:
 * 1. splits top-level text on double <br> into paragraphs,
 * 2. promotes a bold line that opens a paragraph to an h2, but only when the text has no real headings,
 * 3. numbers the top-level headings (keeping the text's own numbers, e.g. "Section 3", so cross-references still match),
 *    gives them ids and lists them in the TOC,
 * 4. highlights the current section (aria-current) and shows "Back to top" once the header has scrolled away.
 * Without headings it leaves the text alone and hides the TOC column.
 */

const BLOCK_TAGS = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DIV', 'DL', 'FIGURE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'HR', 'OL', 'P', 'PRE', 'SECTION', 'TABLE', 'UL',
]);
const HEADING_TAGS = ['H1', 'H2', 'H3', 'H4'];
const MAX_PSEUDO_HEADING_LENGTH = 120;
const OWN_NUMBER = /^\s*(?:(?:section|article|artikel|abschnitt|art\.|§)\s*)?(\d+(?:\.\d+)*)\s*[-–—.:)]?\s+/i;

/** @param {Node} node */
const isBlank = (node) => node.nodeType === Node.TEXT_NODE && !node.textContent?.trim();
/** @param {Node} node */
const isBr = (node) => node.nodeName === 'BR';
/** @param {Node} node */
const isInline = (node) =>
  node.nodeType === Node.TEXT_NODE || (node.nodeType === Node.ELEMENT_NODE && !BLOCK_TAGS.has(node.nodeName));

/**
 * Splits a list of inline nodes at every run of two or more <br>s. Leading/trailing <br>s and whitespace are dropped.
 * @param {Node[]} nodes
 * @returns {Node[][]}
 */
function splitOnDoubleBr(nodes) {
  /** @type {Node[][]} */
  const chunks = [[]];
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (isBr(node)) {
      let j = i + 1;
      while (j < nodes.length && isBlank(nodes[j])) j++;
      if (j < nodes.length && isBr(nodes[j])) {
        while (j < nodes.length && (isBr(nodes[j]) || isBlank(nodes[j]))) j++;
        chunks.push([]);
        i = j - 1;
        continue;
      }
    }
    chunks[chunks.length - 1]?.push(node);
  }
  return chunks
    .map((chunk) => {
      while (chunk.length && (isBr(chunk[0]) || isBlank(chunk[0]))) chunk.shift();
      while (chunk.length && (isBr(chunk[chunk.length - 1]) || isBlank(chunk[chunk.length - 1]))) chunk.pop();
      return chunk;
    })
    .filter((chunk) => chunk.length > 0);
}

/**
 * Wraps loose top-level inline runs in paragraphs and splits paragraphs on double <br>.
 * @param {HTMLElement} body
 */
function normaliseParagraphs(body) {
  /** @type {Node[]} */
  let run = [];
  const flush = (/** @type {Node | null} */ before) => {
    if (!run.length) return;
    for (const chunk of splitOnDoubleBr(run)) {
      const p = document.createElement('p');
      p.append(...chunk);
      body.insertBefore(p, before);
    }
    for (const node of run) node.parentNode === body && node.remove();
    run = [];
  };

  for (const node of [...body.childNodes]) {
    if (isInline(node)) {
      run.push(node);
      continue;
    }
    flush(node);
    if (node.nodeName === 'P' && node instanceof HTMLElement && node.querySelector(':scope > br')) {
      const chunks = splitOnDoubleBr([...node.childNodes]);
      if (chunks.length > 1) {
        for (const chunk of chunks) {
          const p = document.createElement('p');
          p.append(...chunk);
          body.insertBefore(p, node);
        }
        node.remove();
      }
    }
  }
  flush(null);
}

/**
 * A paragraph that opens with a short bold line followed by a line break (or nothing) reads as a heading:
 * promote that line to an h2 and keep the rest as the paragraph.
 * @param {HTMLElement} body
 */
function promoteBoldHeadings(body) {
  for (const p of body.querySelectorAll(':scope > p')) {
    const nodes = [...p.childNodes].filter((node) => !isBlank(node));
    const [first, second] = nodes;
    if (!first || !(first.nodeName === 'STRONG' || first.nodeName === 'B')) continue;
    if (second && !isBr(second)) continue;

    const text = first.textContent?.replace(/\s+/g, ' ').trim() ?? '';
    if (!text || text.length > MAX_PSEUDO_HEADING_LENGTH) continue;

    const heading = document.createElement('h2');
    heading.textContent = text;
    p.before(heading);
    first.remove();
    if (second) second.remove();
    if (!p.textContent?.trim() && !p.querySelector('img, a')) p.remove();
  }
}

/**
 * Text in capitals reads aggressive next to the brand (D26). Shown in sentence case; the words stay the same.
 * @param {string} text
 */
function softenCaps(text) {
  const letters = text.replace(/[^\p{L}]/gu, '');
  const upper = letters.replace(/[^\p{Lu}]/gu, '');
  if (letters.length < 4 || upper.length / letters.length <= 0.5) return text;
  const lower = text.toLocaleLowerCase();
  return lower.charAt(0).toLocaleUpperCase() + lower.slice(1);
}

/** @param {string} text */
function slugify(text) {
  return (
    text
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section'
  );
}

class BigensLegalPage extends HTMLElement {
  /** @type {{ heading: HTMLElement, link: HTMLAnchorElement }[]} */
  #sections = [];
  #rafId = 0;
  /** @type {IntersectionObserver | null} */
  #headerObserver = null;
  /** @type {HTMLElement[] | null} */
  #headings = null;

  connectedCallback() {
    const body = /** @type {HTMLElement | null} */ (this.querySelector('[data-legal-body]'));
    const layout = this.querySelector('[data-legal-layout]');
    const aside = /** @type {HTMLElement | null} */ (this.querySelector('[data-legal-aside]'));
    const list = this.querySelector('[data-legal-toc]');

    this.#revealCurrentDoc();

    // Prepare once: the text is rewritten in place, so a re-connect (element moved) must not process it again.
    this.#headings ??= body ? this.#prepare(body) : [];
    const headings = this.#headings;
    if (!headings.length || !list || !aside) {
      layout?.classList.remove('bigens-legal__layout--toc');
      if (aside) aside.hidden = true;
      return;
    }

    this.#buildToc(headings, list);
    layout?.classList.add('bigens-legal__layout--toc');
    aside.hidden = false;

    this.querySelector('[data-legal-toggle]')?.addEventListener('click', this.#toggle);
    list.addEventListener('click', this.#onTocClick);
    // Horizon scrolls inside .page-wrapper on desktop and the window on mobile; scroll events don't bubble, so capture them.
    document.addEventListener('scroll', this.#onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#updateCurrent();
    this.#observeHeader();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.#onScroll, { capture: true });
    window.removeEventListener('resize', this.#onScroll);
    this.#headerObserver?.disconnect();
    cancelAnimationFrame(this.#rafId);
  }

  /** On small screens the legal-docs tabs scroll sideways; bring the current one into view without scrolling the page. */
  #revealCurrentDoc() {
    const current = /** @type {HTMLElement | null} */ (this.querySelector('.bigens-legal__doc[aria-current]'));
    const list = current?.closest('ul');
    if (!current || !list || list.scrollWidth <= list.clientWidth) return;
    const offset = current.getBoundingClientRect().left - list.getBoundingClientRect().left;
    list.scrollLeft += offset - (list.clientWidth - current.offsetWidth) / 2;
  }

  /**
   * @param {HTMLElement} body
   * @returns {HTMLElement[]} the top-level headings
   */
  #prepare(body) {
    normaliseParagraphs(body);
    for (const table of body.querySelectorAll('table')) {
      if (table.parentElement?.classList.contains('rte-table-wrapper')) continue;
      const wrapper = document.createElement('div');
      wrapper.className = 'rte-table-wrapper';
      table.before(wrapper);
      wrapper.append(table);
    }
    if (!body.querySelector(HEADING_TAGS.join(','))) promoteBoldHeadings(body);

    const level = HEADING_TAGS.find((tag) => body.querySelector(tag));
    if (!level) return [];

    // Top-level headings only; nested ones (inside lists, tables) are part of the text, not the outline.
    const headings = /** @type {HTMLElement[]} */ (
      [...body.querySelectorAll(level)].filter((heading) => heading.parentElement === body)
    );
    const subLevel = HEADING_TAGS[HEADING_TAGS.indexOf(level) + 1];
    if (subLevel) {
      for (const sub of body.querySelectorAll(`:scope > ${subLevel}`)) sub.classList.add('bigens-legal__subheading');
    }

    const numbering = this.dataset.numbering !== 'false';
    // If most headings carry their own number ("Section 3 - …"), keep those numbers so references in the text still match.
    const ownNumbers = headings.map((heading) => heading.textContent?.match(OWN_NUMBER)?.[1] ?? null);
    const useOwn = ownNumbers.filter(Boolean).length >= headings.length / 2;
    const usedIds = new Set([...document.querySelectorAll('[id]')].map((element) => element.id));

    headings.forEach((heading, index) => {
      let text = (heading.textContent ?? '').replace(/\s+/g, ' ').trim();
      if (useOwn) text = text.replace(OWN_NUMBER, '');
      text = softenCaps(text);
      const number = useOwn ? ownNumbers[index] : String(index + 1);

      if (!heading.id) {
        let id = `legal-${slugify(text)}`;
        for (let n = 2; usedIds.has(id); n++) id = `legal-${slugify(text)}-${n}`;
        heading.id = id;
      }
      usedIds.add(heading.id);

      heading.classList.add('bigens-legal__heading');
      heading.dataset.legalText = text;
      heading.dataset.legalNumber = numbering && number ? number : '';
      heading.replaceChildren();
      if (heading.dataset.legalNumber) {
        const num = document.createElement('span');
        num.className = 'bigens-legal__num';
        num.textContent = `${heading.dataset.legalNumber}.`;
        heading.append(num, ' ');
      }
      heading.append(text);
    });

    return headings;
  }

  /**
   * @param {HTMLElement[]} headings
   * @param {Element} list
   */
  #buildToc(headings, list) {
    list.replaceChildren();
    this.#sections = headings.map((heading) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'bigens-legal__toc-link';
      link.href = `#${heading.id}`;
      if (heading.dataset.legalNumber) {
        const num = document.createElement('span');
        num.className = 'bigens-legal__toc-num';
        num.textContent = `${heading.dataset.legalNumber}.`;
        link.append(num);
      }
      const label = document.createElement('span');
      label.textContent = heading.dataset.legalText ?? '';
      link.append(label);
      item.append(link);
      list.append(item);
      return { heading, link };
    });
  }

  #toggle = () => {
    const button = this.querySelector('[data-legal-toggle]');
    if (!button) return;
    button.setAttribute('aria-expanded', String(button.getAttribute('aria-expanded') !== 'true'));
  };

  /** @param {Event} event */
  #onTocClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('a')) return;
    // Collapse the mobile list before the jump, so the target position is measured without it.
    this.querySelector('[data-legal-toggle]')?.setAttribute('aria-expanded', 'false');
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => this.#updateCurrent());
  };

  #updateCurrent() {
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const line = headerHeight + Math.min(window.innerHeight * 0.25, 160);
    let current = this.#sections[0];
    for (const section of this.#sections) {
      if (section.heading.getBoundingClientRect().top <= line) current = section;
      else break;
    }
    // At the very bottom the last sections can't reach the line; mark the last one.
    const wrapper = document.querySelector('.page-wrapper');
    const scroller =
      wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 && getComputedStyle(wrapper).overflowY !== 'visible'
        ? wrapper
        : document.scrollingElement;
    if (scroller && scroller.scrollTop > 0 && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2) {
      current = this.#sections[this.#sections.length - 1];
    }
    for (const section of this.#sections) {
      if (section === current) section.link.setAttribute('aria-current', 'location');
      else section.link.removeAttribute('aria-current');
    }
  }

  #observeHeader() {
    const header = this.querySelector('.bigens-legal__header');
    const top = /** @type {HTMLElement | null} */ (this.querySelector('[data-legal-top]'));
    if (!header || !top || !('IntersectionObserver' in window)) return;
    this.#headerObserver = new IntersectionObserver(([entry]) => {
      top.hidden = Boolean(entry?.isIntersecting);
    });
    this.#headerObserver.observe(header);
  }
}

if (!customElements.get('bigens-legal-page')) customElements.define('bigens-legal-page', BigensLegalPage);
